import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { SportsSoccer as BallIcon } from '@mui/icons-material';
import { COLORS, SECTION_STYLES } from './MAMCStyles';

const TopScorers = ({ scorers, teams }) => {
  // Group scorers by player and count their goals
  const scorerStats = scorers.reduce((acc, scorer) => {
    const playerId = scorer.player.id;
    if (!acc[playerId]) {
      // Find the team by checking each team's players array
      const team = teams.find(t => t.players?.some(p => p.id === playerId));

      acc[playerId] = {
        player: scorer.player,
        goals: 0,
        team: team,
      };
    }
    acc[playerId].goals += 1;
    return acc;
  }, {});

  const sortedScorers = Object.values(scorerStats)
    .sort((a, b) => b.goals - a.goals)
    .slice(0, 5);

  return (
    <Box
      sx={{
        ...SECTION_STYLES.topScorers,
        height: '100%',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: { xs: 2, sm: 4 },
          pb: 2,
          borderBottom: `2px solid ${COLORS.background.light}`,
        }}
      >
        <BallIcon
          sx={{
            color: COLORS.secondary,
            fontSize: { xs: 24, sm: 28 },
            filter: 'drop-shadow(0 2px 4px rgba(218,165,32,0.3))',
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: COLORS.text.accent,
            fontWeight: 'bold',
            fontSize: { xs: '1rem', sm: '1.25rem' },
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            letterSpacing: '1px',
          }}
        >
          Top Scorers
        </Typography>
      </Box>

      {sortedScorers.map((scorer, index) => (
        <Box
          key={scorer.player.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            py: { xs: 1.5, sm: 2 },
            borderBottom:
              index < sortedScorers.length - 1 ? `1px solid ${COLORS.background.light}` : 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateX(4px)',
              backgroundColor: 'rgba(218,165,32,0.1)',
            },
          }}
        >
          <Typography
            sx={{
              color: index === 0 ? COLORS.secondary : COLORS.text.primary,
              fontWeight: 'bold',
              fontSize: { xs: '1rem', sm: '1.2rem' },
              width: { xs: '24px', sm: '30px' },
              textShadow: index === 0 ? '0 2px 4px rgba(218,165,32,0.3)' : 'none',
            }}
          >
            {index + 1}
          </Typography>

          <Avatar
            sx={{
              bgcolor: index === 0 ? COLORS.secondary : COLORS.background.light,
              color: index === 0 ? COLORS.background.dark : COLORS.text.primary,
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
              fontWeight: 'bold',
              boxShadow: index === 0 ? '0 2px 8px rgba(218,165,32,0.4)' : 'none',
            }}
          >
            {scorer.player.player_name.charAt(0)}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: COLORS.text.primary,
                fontWeight: index === 0 ? 'bold' : 'normal',
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {scorer.player.player_name}
            </Typography>
            <Typography
              sx={{
                color: COLORS.text.secondary,
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {scorer.team?.team_name || ''}
            </Typography>
          </Box>

          <Typography
            sx={{
              color: index === 0 ? COLORS.secondary : COLORS.text.primary,
              fontWeight: 'bold',
              fontSize: { xs: '1.2rem', sm: '1.4rem' },
              width: { xs: '30px', sm: '40px' },
              textAlign: 'right',
              textShadow: index === 0 ? '0 2px 4px rgba(218,165,32,0.3)' : 'none',
            }}
          >
            {scorer.goals}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default TopScorers;
